import { createFieldPopup } from './field-popup.js';

interface Options {
  searchable?: boolean;
  groups?: readonly { label: string; options: readonly { value: string; label?: string }[] }[];
  exclusiveValues?: readonly string[];
  selectedText?(count: number): string;
  removeLabel?(label: string): string;
  doneLabel?: string;
  label: string;
  searchLabel: string;
  emptyText: string;
  resultsText(shown: number, total: number): string;
}

const normalize = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
let nextId = 0;
const resultLimit = 100;

/** Presentation adapter: the native select remains the sole submitted field. */
export function createSearchSelect(select: HTMLSelectElement, options: Options) {
  const searchable = options.searchable !== false;
  const multiple = select.multiple;
  const exclusiveValues = new Set(options.exclusiveValues);
  const id = `search-select-${++nextId}`;
  const original = new Map(['id', 'tabindex', 'aria-hidden', 'class'].map(name => [name, select.getAttribute(name)]));
  const element = document.createElement('div'); element.className = multiple ? 'search-select search-select-multiple' : 'search-select';
  const button = document.createElement('button'); button.type = 'button'; button.className = 'search-select-trigger';
  button.id = select.id || `${id}-trigger`;
  button.setAttribute('aria-haspopup', 'dialog'); button.setAttribute('aria-controls', id); button.setAttribute('aria-expanded', 'false');
  const value = document.createElement('span'); value.className = 'search-select-value';
  const chevron = document.createElement('span'); chevron.className = 'search-select-chevron'; chevron.setAttribute('aria-hidden', 'true');
  button.append(value, chevron);
  // Install in place before moving the select, so labels keep their original target.
  select.before(element);
  select.id = `${id}-native`; select.tabIndex = -1; select.setAttribute('aria-hidden', 'true'); select.classList.add('search-select-native');
  element.append(select, button);
  const chips = document.createElement('div'); chips.className = 'search-select-chips';
  if (multiple) element.append(chips);

  const panel = document.createElement('div'); panel.id = id; panel.className = 'search-select-popup';
  if (multiple) panel.classList.add('search-select-popup-multiple');
  panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-label', options.label);
  const search = document.createElement('input'); search.type = 'search'; search.className = 'field-popup-search';
  search.placeholder = options.searchLabel; search.setAttribute('aria-label', options.searchLabel);
  search.autocomplete = 'off'; search.spellcheck = false; search.setAttribute('role', 'combobox');
  search.setAttribute('aria-autocomplete', 'list'); search.setAttribute('aria-expanded', 'false');
  const list = document.createElement('div'); list.id = `${id}-list`; list.className = 'field-popup-list';
  list.setAttribute('role', 'listbox'); list.setAttribute('aria-label', options.label); search.setAttribute('aria-controls', list.id);
  if (multiple) list.setAttribute('aria-multiselectable', 'true');
  const status = document.createElement('p'); status.className = 'field-popup-empty'; status.setAttribute('role', 'status');
  if (!searchable) list.tabIndex = 0;
  const keyboardControl = searchable ? search : list;
  if (searchable) panel.append(search);
  panel.append(list, status);
  const done = document.createElement('button'); done.type = 'button'; done.className = 'search-select-done'; done.textContent = options.doneLabel ?? '';
  if (multiple && options.doneLabel) panel.append(done);
  let open = false, disposed = false, nativeInvalid = false, active = -1;
  let shown: HTMLOptionElement[] = [];
  let rows: HTMLButtonElement[] = [];
  const disabled = () => select.matches(':disabled');
  const unavailable = (option: HTMLOptionElement) => option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled);
  const close = (restore = false) => {
    open = false; popup.hide(); button.setAttribute('aria-expanded', 'false'); search.setAttribute('aria-expanded', 'false');
    keyboardControl.removeAttribute('aria-activedescendant');
    if (restore && button.isConnected && !disabled()) button.focus();
  };
  const popup = createFieldPopup(button, panel, { minWidth: 260, maxWidth: 440, onDismiss: () => close() });
  const setActive = (index: number) => {
    active = index;
    for (const [i, row] of rows.entries()) row.setAttribute('data-active', String(i === active));
    const row = rows[active];
    if (row) { keyboardControl.setAttribute('aria-activedescendant', row.id); row.scrollIntoView?.({ block: 'nearest' }); }
    else keyboardControl.removeAttribute('aria-activedescendant');
  };
  const notifyChange = () => {
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const choose = (index: number) => {
    const option = shown[index];
    if (!open || !option || disabled() || unavailable(option) || !select.contains(option)) return;
    if (multiple) {
      const selected = !option.selected;
      for (const candidate of Array.from(select.options)) {
        if (selected && (exclusiveValues.has(option.value) || exclusiveValues.has(candidate.value) || candidate.value === '')) candidate.selected = false;
      }
      option.selected = selected;
      sync(); notifyChange(); keyboardControl.focus();
      return;
    }
    const changed = select.selectedIndex !== option.index;
    select.selectedIndex = option.index;
    sync(); close(true);
    if (changed) {
      notifyChange();
    }
  };
  const presentation = (option: HTMLOptionElement) => {
    for (const group of options.groups ?? []) {
      const entry = group.options.find(entry => entry.value === option.value);
      if (entry) return { group, label: entry.label ?? option.label };
    }
    return { group: undefined, label: option.label };
  };
  const render = (preserveActive = false) => {
    const previous = preserveActive ? shown[active] : undefined;
    const query = searchable ? normalize(search.value) : '', terms = query.split(/\s+/).filter(Boolean);
    const matches = Array.from(select.options).filter(option => {
      const { group, label } = presentation(option);
      return !option.hidden && (!multiple || option.value !== '') && terms.every(term => normalize(`${group?.label ?? ''} ${label} ${option.label}`).includes(term));
    });
    if (options.groups) {
      const order = new Map(options.groups.flatMap(group => group.options).map((entry, index) => [entry.value, index]));
      matches.sort((a, b) => (order.get(a.value) ?? Infinity) - (order.get(b.value) ?? Infinity));
    }
    if (query && !options.groups) matches.sort((a, b) => Number(normalize(b.label) === query) - Number(normalize(a.label) === query));
    shown = searchable && !multiple ? matches.slice(0, resultLimit) : matches;
    const selectedOption = matches.find(option => option.selected);
    if (!query && selectedOption && !shown.includes(selectedOption)) shown[shown.length - 1] = selectedOption;
    rows = shown.map((option, index) => {
      const row = document.createElement('button'); row.type = 'button'; row.className = 'field-popup-option'; row.tabIndex = -1;
      row.id = `${id}-option-${option.index}`; row.setAttribute('role', 'option'); row.setAttribute('aria-selected', String(option.selected));
      row.disabled = unavailable(option); row.setAttribute('aria-disabled', String(row.disabled));
      const label = document.createElement('span'); label.className = 'search-select-option-label'; label.textContent = presentation(option).label;
      const check = document.createElement('span'); check.className = 'search-select-check'; check.setAttribute('aria-hidden', 'true'); check.textContent = option.selected ? '✓' : '';
      row.append(label, check);
      // Leave touch pointer handling to the browser, allowing the list to scroll.
      row.addEventListener('pointerdown', event => { if (event.pointerType === 'mouse') event.preventDefault(); });
      row.addEventListener('click', () => choose(index));
      return row;
    });
    list.replaceChildren();
    const groupElements = new Map<NonNullable<ReturnType<typeof presentation>['group']>, HTMLElement>();
    for (const [index, option] of shown.entries()) {
      const { group } = presentation(option);
      if (!group) { list.append(rows[index]!); continue; }
      let section = groupElements.get(group);
      if (!section) {
        section = document.createElement('div'); section.className = 'search-select-group'; section.setAttribute('role', 'group'); section.setAttribute('aria-label', group.label);
        const heading = document.createElement('div'); heading.className = 'search-select-group-heading'; heading.textContent = group.label; heading.setAttribute('aria-hidden', 'true');
        section.append(heading); groupElements.set(group, section); list.append(section);
      }
      section.append(rows[index]!);
    }
    status.hidden = matches.length > 0 && (!searchable || multiple || matches.length <= resultLimit);
    status.textContent = matches.length ? options.resultsText(shown.length, matches.length) : options.emptyText;
    const selected = shown.findIndex(option => option.selected && !unavailable(option));
    setActive(previous && shown.includes(previous) && !unavailable(previous) ? shown.indexOf(previous) : query ? shown.findIndex(option => !unavailable(option)) : selected);
    popup.reposition();
  };
  function sync() {
    if (disposed) return;
    const selectedOptions = Array.from(select.options).filter(option => option.selected && option.value !== '');
    const text = multiple ? options.selectedText?.(selectedOptions.length) ?? (selectedOptions.length ? String(selectedOptions.length) : options.label) : select.options[select.selectedIndex]?.label ?? '';
    if (value.textContent !== text) value.textContent = text;
    button.disabled = disabled();
    if (multiple) {
      // Avoid replacing focused chips during unrelated synchronization.
      const signature = JSON.stringify(selectedOptions.map(option => [option.index, option.label, unavailable(option), button.disabled]));
      if (chips.dataset.selection !== signature) {
        chips.dataset.selection = signature;
        chips.replaceChildren(...selectedOptions.map(option => {
          const chip = document.createElement('button'); chip.type = 'button'; chip.className = 'search-select-chip';
          chip.disabled = button.disabled || unavailable(option);
          chip.setAttribute('aria-label', options.removeLabel?.(option.label) ?? option.label);
          const label = document.createElement('span'); label.textContent = option.label;
          const icon = document.createElement('span'); icon.setAttribute('aria-hidden', 'true'); icon.textContent = '×'; chip.append(label, icon);
          chip.addEventListener('click', () => {
            if (disposed || disabled() || unavailable(option) || !select.contains(option) || !option.selected) return;
            option.selected = false; sync(); notifyChange(); button.focus();
          });
          return chip;
        }));
      }
      chips.hidden = selectedOptions.length === 0;
    }
    button.setAttribute('aria-required', String(select.required));
    if (select.validity.valid) nativeInvalid = false;
    for (const name of ['aria-invalid', 'aria-describedby', 'aria-labelledby']) {
      const attribute = select.getAttribute(name);
      if (attribute !== null) button.setAttribute(name, attribute);
      else if (name === 'aria-invalid' && nativeInvalid) button.setAttribute(name, 'true');
      else button.removeAttribute(name);
    }
    if (!button.hasAttribute('aria-labelledby')) button.setAttribute('aria-label', `${options.label}: ${text}`);
    if (open && (button.disabled || !element.isConnected || !button.getClientRects().length)) close();
    else if (open) render(multiple);
  }
  const show = () => {
    sync(); if (disabled() || disposed) return;
    open = true; search.value = ''; render(); popup.show();
    if (!open) return;
    button.setAttribute('aria-expanded', 'true'); search.setAttribute('aria-expanded', 'true'); keyboardControl.focus(); setActive(active);
  };
  const toggle = () => { if (open) close(true); else show(); };
  const onTriggerKey = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); show(); }
  };
  const onSearchKey = (event: KeyboardEvent) => {
    if (event.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); event.stopPropagation();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      let next = active < 0 ? (direction > 0 ? -1 : 0) : active;
      for (let count = 0; count < shown.length; count++) {
        next = (next + direction + shown.length) % shown.length;
        if (!unavailable(shown[next]!)) { setActive(next); break; }
      }
    } else if ((!searchable || (multiple && search.value === '')) && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault(); event.stopPropagation();
      const available = shown.map((option, index) => ({ option, index })).filter(({ option }) => !unavailable(option));
      setActive((event.key === 'Home' ? available[0] : available.at(-1))?.index ?? -1);
    } else if (event.key === 'Enter' || (multiple && event.key === ' ' && (!searchable || search.value === ''))) { event.preventDefault(); event.stopPropagation(); choose(active); }
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
    else if (event.key === 'Tab') {
      if (multiple && options.doneLabel && !event.shiftKey) { event.preventDefault(); done.focus(); }
      else close(true);
    }
  };
  const onDone = () => close(true);
  const onDoneKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
    else if (event.key === 'Tab') {
      if (event.shiftKey) { event.preventDefault(); keyboardControl.focus(); }
      else close(true);
    }
  };
  const onSearchInput = () => render();
  const forwardFocus = () => { sync(); if (!disabled()) button.focus(); };
  const onInvalid = () => { nativeInvalid = true; forwardFocus(); };
  const onReset = () => { queueMicrotask(() => { nativeInvalid = false; sync(); }); };
  const form = select.form;
  button.addEventListener('click', toggle); button.addEventListener('keydown', onTriggerKey);
  search.addEventListener('input', onSearchInput); keyboardControl.addEventListener('keydown', onSearchKey);
  done.addEventListener('click', onDone); done.addEventListener('keydown', onDoneKey);
  select.addEventListener('focus', forwardFocus); select.addEventListener('invalid', onInvalid); select.addEventListener('change', sync); select.addEventListener('input', sync);
  form?.addEventListener('reset', onReset);
  const observer = new MutationObserver(records => {
    if (records.some(record => select.contains(record.target) || (record.target instanceof Element && record.target.contains(element))) || !element.isConnected) sync();
  });
  observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'required', 'aria-invalid', 'aria-describedby', 'aria-labelledby', 'hidden', 'style', 'class', 'label', 'selected'] });
  sync();
  return {
    element,
    sync,
    dispose() {
      if (disposed) return;
      disposed = true; observer.disconnect(); close(); popup.dispose();
      button.removeEventListener('click', toggle); button.removeEventListener('keydown', onTriggerKey);
      search.removeEventListener('input', onSearchInput); keyboardControl.removeEventListener('keydown', onSearchKey);
      done.removeEventListener('click', onDone); done.removeEventListener('keydown', onDoneKey);
      select.removeEventListener('focus', forwardFocus); select.removeEventListener('invalid', onInvalid); select.removeEventListener('change', sync); select.removeEventListener('input', sync);
      form?.removeEventListener('reset', onReset);
      if (element.parentNode) element.replaceWith(select); else select.remove();
      for (const [name, attribute] of original) {
        if (attribute === null) select.removeAttribute(name); else select.setAttribute(name, attribute);
      }
    },
  };
}
